import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { FlexLayoutModule } from "ngx-flexible-layout";
import {
  AbcPopupComponent,
  AbcToastService,
  _AbcButtonComponent,
  _AbcGridColumn,
  _AbcGridComponent,
  _AbcSelectBoxComponent,
} from "@barthauer/abc/ui-components";
import {
  LookupItem,
  NotificationMessage,
  ToastType,
} from "@barthauer/custom/xmt-models";
import { DxCheckBoxModule, DxDataGridModule } from "devextreme-angular";
import { DxoSelectionModule } from "devextreme-angular/ui/nested";
import { formatMessage } from "devextreme/localization";
import dxCheckBox, {
  InitializedEvent,
  ValueChangedEvent,
} from "devextreme/ui/check_box";
import dxDataGrid, { SelectionChangedEvent } from "devextreme/ui/data_grid";
import { of, Subscription } from "rxjs";
import { finalize, switchMap } from "rxjs/operators";

import { DataService } from "../../../services/data.service";
import { MeasureService } from "../../measure/measure.service";
import { NotificationDetailsComponent } from "../notification-details/notification-details.component";

import { ConfirmationPopupComponent } from "../../../shared/confirmation-popup/confirmation-popup.component";
import {
  INotificationItem,
  NotificationCategory,
  NotificationService1,
} from "../../../services/notification1.service";
import { ToastService } from "../../../services/toast.service";
import { AppService } from "../../../services/app.service";
import { ScreenService } from "../../../services/screen.service";
import { BreakpointObserver } from "@angular/cdk/layout";

export const notificationPrefixMap: Record<NotificationCategory, string> = {
  [NotificationCategory.APPROVAL_REQUEST]: "AR-",
  [NotificationCategory.APPROVAL_STEP]: "AS-",
  [NotificationCategory.MEASURE_TASK]: "MT-",
  [NotificationCategory.MEASURE]: "M-",
  [NotificationCategory.MEASURE_PROGRESS]: "MP-",
};

enum NotificationFilterType {
  All = "0",
  Read = "1",
  Unread = "2",
}

@Component({
  selector: "notification-list",
  templateUrl: "./notification-list.component.html",
  styleUrls: ["./notification-list.component.scss"],
  encapsulation: ViewEncapsulation.None,
  imports: [
    DxDataGridModule,
    _AbcGridComponent,
    DxoSelectionModule,
    DxCheckBoxModule,
    NotificationDetailsComponent,
    CommonModule,
    FlexLayoutModule,
    _AbcSelectBoxComponent,
    _AbcButtonComponent,
    ConfirmationPopupComponent,
  ],
})
export class NotificationListComponent
  implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild("notificationGrid") notificationGrid: _AbcGridComponent;
  @ViewChild("notificationDetails")
  notificationDetails: NotificationDetailsComponent;
  @ViewChild("readNotificationPopup") readNotificationPopup: AbcPopupComponent;
  @ViewChild('detailsContainer') detailsContainer: ElementRef;
  private readonly notificationService = inject(NotificationService1);
  private readonly toastService = inject(ToastService);
  public dataService = inject(DataService);

  protected resourceManager: (key: string, ...value: string[]) => string =
    this.dataService.res;

  private subscriptions: Subscription[] = [];

  protected showLoader: boolean = true;

  protected formatMessage: (key: string, ...values: Array<string>) => string =
    formatMessage;
  protected notificationGridColumns: _AbcGridColumn[] =
    this.defineNotificationGridColumns();
  protected allNotifications: INotificationItem[] = [];
  protected filteredNotifications: INotificationItem[] = [];
  protected notificationFilterOptions: LookupItem[] =
    this.getNotificationFilterOptions();
  protected selectedFilterId: NotificationFilterType =
    NotificationFilterType.Unread;
  protected notificationItem: INotificationItem;
  protected selectedKeys = [];
  protected selectAllCheckBox: dxCheckBox;
  protected showEditTask: boolean = false;
  protected checkBoxUpdating: boolean = false;
  private isSmallScreen = false;
  constructor(private readonly screenService: ScreenService,
    private breakpointObserver: BreakpointObserver

  ) { }
  ngOnInit(): void {
    this.loadNotifications();
    this.subscriptions.push(
      this.breakpointObserver.observe(['(max-width: 900px)'])
        .subscribe(result => {
          this.isSmallScreen = result.matches;
        })
    );
  }
  private scrollToDetailsOnSmallScreen(): void {
    const screenWidth = window.innerWidth;

    if (screenWidth <= 700 && this.detailsContainer) {
      this.detailsContainer.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      });
    }
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.screenService.screen$.subscribe(({ screenSize, isMenuOpened }) => {
        this.screenService.setGridColumnsWidth(
          this.notificationGrid.columns,
          this.notificationGrid,
          screenSize,
          isMenuOpened,
          this.getColumnWidth
        );
      })
    );
  }
  private getColumnWidth = (
    key: string,
    isMenuOpened: boolean,
  ): string => {
    const baseWidths = {
      Comment: {
        menuOpen: '',
        menuClosed: '',
      },
      Id: {
        menuOpen: '200px',
        menuClosed: '250px',
      },
      SysDateTimeInsert: {
        menuOpen: '100px',
        menuClosed: '150px',
      },
    };

    switch (key) {
      case 'Comment':
        return isMenuOpened
          ? baseWidths.Comment.menuOpen
          : baseWidths.Comment.menuClosed;

      case 'Id':
        return isMenuOpened
          ? baseWidths.Id.menuOpen
          : baseWidths.Id.menuClosed;

      case 'SysDateTimeInsert':
        return isMenuOpened
          ? baseWidths.SysDateTimeInsert.menuOpen
          : baseWidths.SysDateTimeInsert.menuClosed;


    }
  };

  protected onNotificationFilterChange(): void {
    this.filteredNotifications = this.filterNotifications();
  }

  protected viewNotificationDetails(event: any): void {
    this.notificationGrid.instance.selectRowsByIndexes(event?.rowIndex);
    this.notificationItem = event?.data;
    this.showEditTask = true;
    this.notificationGrid.instance.refresh();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.scrollToDetailsOnSmallScreen();
      });
    });
  }
  public onCellClick(e) { }
  protected onSelectionChanged(e: SelectionChangedEvent<any, number>) {
    const deselectRowKeys: number[] = [];
    const dataGrid = e.component;

    this.selectedKeys = [];

    e.selectedRowsData.forEach((item) => {
      if (item.Reminder.LastVisitedDate) {
        deselectRowKeys.push(dataGrid.keyOf(item));
      } else {
        this.selectedKeys.push(item);
      }
    });

    if (deselectRowKeys.length) {
      dataGrid.deselectRows(deselectRowKeys);
    }

    this.checkBoxUpdating = true;
    this.selectAllCheckBox.option("value", this.isSelectAll(dataGrid));
    this.checkBoxUpdating = false;

    this.showEditTask = this.selectedKeys.length === 1;
    this.notificationItem = this.showEditTask ? this.selectedKeys[0] : null;
  }

  protected onEditorPreparing(e: any) {
    if (e.parentType === "headerRow") {
      const dataGrid = e.component;
      e.editorOptions.value = this.isSelectAll(dataGrid);

      e.editorOptions.onInitialized = (e: InitializedEvent) => {
        if (e.component) this.selectAllCheckBox = e.component;
      };

      e.editorOptions.onValueChanged = (e: ValueChangedEvent) => {
        if (!e.event) {
          if (e.previousValue && !this.checkBoxUpdating) {
            e.component.option("value", e.previousValue);
          }
          return;
        }

        if (this.isSelectAll(dataGrid) === e.value) {
          return;
        }

        e.value ? dataGrid.selectAll() : dataGrid.deselectAll();
        e.event.preventDefault();
      };
    }

    if (e.parentType === "dataRow") {
      e.editorOptions.disabled = e.row.data.Reminder.LastVisitedDate !== null;
    }
  }

  protected onHidePopUp(value: boolean): void {
    this.showEditTask = value;
    this.notificationGrid.instance.refresh();
  }

  protected hidePopup(): void {
    this.readNotificationPopup.instance.hide();
  }

  protected get NotificationFilterType(): typeof NotificationFilterType {
    return NotificationFilterType;
  }

  protected markSelectedNotificationsAsRead() {
    this.showLoader = true;
    const ids = this.selectedKeys.map((n) => n.Id);
    this.notificationService.setPollingEnabled(false);

    this.subscriptions.push(
      this.notificationService
        .markNotificationsAsReadByIds(ids)
        .pipe(
          finalize(() => {
            this.showLoader = false;
            this.notificationService.setPollingEnabled(true);
          })
        )
        .subscribe({
          next: () => {
            this.selectedKeys.forEach((element) => {
              element.Reminder.LastVisitedDate = new Date();
            });
            this.handleUpdateNotification(this.selectedKeys);

            this.notificationGrid.instance.deselectAll();
            this.selectedKeys = [];
            this.toastService.toast(
              this.dataService.res(NotificationMessage.UpdatedSuccessfully),
              ToastType.SUCCESS
            );
          },
          error: () => {
            this.toastService.toast(
              NotificationMessage.ErrorSomethingWentWrong,
              ToastType.ERROR
            );
          },
        })
    );
  }

  protected handleUpdateNotification(notificationList: INotificationItem[]) {
    notificationList.forEach((notification) => {
      const filtredNotification = this.allNotifications.find(
        (n) => n.Id === notification.Id
      );
      if (filtredNotification) {
        filtredNotification.Reminder.IsActive = notification.Reminder.IsActive;
        filtredNotification.Reminder.LastVisitedDate =
          notification.Reminder.LastVisitedDate;
        this.allNotifications = [...this.allNotifications];
      }
    });

    this.filteredNotifications = [...this.filterNotifications()];
  }

  private defineNotificationGridColumns(): _AbcGridColumn[] {
    return [
      {
        caption: "ID",
        dataField: "Id",
        hidingPriority: 2,
        minWidth: 150,
        calculateCellValue: (rowData: INotificationItem) => {
          return this.generateNotificationIdentifier(
            rowData.Category,
            rowData.SysDateTimeInsert.toString()
          );
        },
      },
      {
        caption: this.dataService.res("designation"),
        dataField: "Comment",
        encodeHtml: false,
        hidingPriority: 1,
        minWidth: 300,
        calculateCellValue: (rowData: INotificationItem) => {

          return `<div style="display: inline-flex; flex-wrap: wrap; align-items: center;">
            <span style="white-space: normal;">${rowData.Comment}</span>
          </div>`;
        },
      },

      {
        caption: this.dataService.res("date"),
        dataField: "SysDateTimeInsert",
        hidingPriority: 1,
        minWidth: 200,
        dataType: "date"

      },
    ];
  }

  private generateNotificationIdentifier(
    source: NotificationCategory,
    date: string
  ): string {
    const prefix = notificationPrefixMap[source];
    if (prefix === undefined) {
      throw new Error(`No prefix defined for NotificationSource: ${source}`);
    }
    return `${this.dataService.getIdentifier(prefix, date)}`;
  }

  private getNotificationFilterOptions(): LookupItem[] {
    return [
      {
        value: "0",
        name: this.dataService.res("notification-allNotifications"),
      },
      {
        value: "1",
        name: this.dataService.res(
          "notification-notificationsThatHaveBeenRead"
        ),
      },
      {
        value: "2",
        name: this.dataService.res("notification-unreadNotifications"),
      },
    ];
  }

  private loadNotifications(): void {
    this.showLoader = true;

    this.subscriptions.push(
      this.notificationService
        .getNotificationList()
        .pipe(
          switchMap((notificationList) => {
            this.allNotifications = notificationList;
            this.onNotificationFilterChange();

            return of(true);
          }),
          finalize(() => {
            this.showLoader = false;
          })
        )
        .subscribe({
          error() {
            this.toastService.toast(
              NotificationMessage.ErrorSomethingWentWrong,
              ToastType.ERROR
            );
          },
        })
    );
  }

  private filterNotifications(): INotificationItem[] {
    if (!this.allNotifications) return [];

    const data = this.allNotifications.filter((notification) => {
      const lastVisitedDate = notification.Reminder?.LastVisitedDate;

      switch (this.selectedFilterId) {
        case NotificationFilterType.All:
          return true;
        case NotificationFilterType.Unread:
          return !lastVisitedDate;
        case NotificationFilterType.Read:
          return !!lastVisitedDate;
        default:
          return false;
      }
    });

    return data;
  }

  private isSelectAll(dataGrid: dxDataGrid): boolean {
    const selectableItems = this.allNotifications?.filter(
      (notification) => notification.Reminder.LastVisitedDate === null
    );
    const selectedRowKeys = dataGrid.option("selectedRowKeys");

    if (!selectedRowKeys?.length) {
      return false;
    }
    return selectedRowKeys.length >= selectableItems.length;
  }
}
